import { copyToClipboard } from '@/helpers/copyToClipboard';
import useToast from '@/hooks/useToast';
import moment from 'moment';
import { useRouter } from 'next/router';
import { useContext, useEffect, useState } from 'react';
import { BsDot } from 'react-icons/bs';
import { useMutation } from 'react-query';
import {
  postIncreaseShareCount,
  putVotePost,
} from '../../../../apis/tms/post.api';
import { convertQuantity } from '../../../../common/quantityHelpers';
import { PATH_NAME } from '../../../../configs/pathName';
import { PostContext } from '../../../../context/PostContext';
import useDebounce from '../../../../hooks/useDebounce';
import { EPostVoteType } from '../../../../interfaces/post.interface';
import { QuickLoginModalHOC } from '../../../modal/LoginModal2HOC';
import { FilledHeart } from '../../svgIcon/filled';
import {
  OutlinedChatDots,
  OutlinedHeart,
  OutlinedSharedNetwork,
} from '../../svgIcon/outlined';
import UploadApplicationsButton from './component/UploadApplicationsButton';
import PostCommentSection from './PostCommentSection';

const PostFooter = () => {
  const toast = useToast();
  const { group, isLocked, ...postInfo } = useContext(PostContext);
  const [postInfoCountable, setPostInfoCountable] = useState<{
    sharesCount: number;
    commentCount: number;
  }>({
    sharesCount: postInfo.sharesCount,
    commentCount: postInfo.commentCount,
  });

  const router = useRouter();

  /**This open is comment section but on desktop */
  const [openCommentSection, setOpenCommentSection] = useState<boolean>(false);

  const navigateGroup = () =>
    router.push({
      pathname: PATH_NAME.MY_GROUPS_DETAIL,
      query: {
        gid: group?.groupId,
      },
    });

  const handleShare = async () => {
    if (isLocked) {
      navigateGroup();
      return;
    }
    try {
      await postIncreaseShareCount(postInfo.postId);
      setPostInfoCountable((prev) => ({
        ...prev,
        sharesCount: prev.sharesCount + 1,
      }));
    } catch (error) {
      console.log(error);
    }
  };

  const handleOnClickShare = () => {
    const shareUrl = `${window.location.origin}/home/${postInfo.postId}`;

    if (navigator.share) {
      navigator.share({
        url: shareUrl,
      });
      handleShare();
    } else {
      copyToClipboard(shareUrl, () =>
        toast('Đã sao chép liên kết thành công', 'success')
      );
    }
  };

  // const lastSubmittedAt = postInfo.lastSubmittedAt;
  const lastSubmittedAt = postInfo.lastSubmittedAt
    ? moment(postInfo.lastSubmittedAt).format('HH:mm - DD/MM/YYYY')
    : null;

  return (
    <>
      <div className="px-2 pb-4 pt-2">
        <div
          onClick={() =>
            !isLocked
              ? router.pathname !== PATH_NAME.HOME_FEED_POST_DETAIL &&
                router.push({
                  pathname: PATH_NAME.HOME_FEED_POST_DETAIL,
                  query: {
                    pid: postInfo.postId,
                  },
                })
              : undefined
          }
          className="flex items-center px-2 text-left text-xs font-semibold leading-[130%] tracking-[-0.25px] text-grey_helper"
        >
          <p>
            {convertQuantity(postInfoCountable.commentCount || 0)} lượt bình
            luận
          </p>
          <div className="mx-[10px] flex w-[7px] flex-shrink-0 items-center justify-center text-base">
            <BsDot color="#707070" className="flex-shrink-0" />
          </div>
          <p>{convertQuantity(postInfoCountable.sharesCount)} lượt chia sẻ</p>
          {!!lastSubmittedAt && postInfo.allowUploadedFile && (
            <>
              <div className="mx-[10px] flex w-[7px] flex-shrink-0 items-center justify-center text-base">
                <BsDot color="#707070" className="flex-shrink-0" />
              </div>
              <p>Lần nộp cuối {lastSubmittedAt}</p>
            </>
          )}
        </div>
        <div className="my-3 h-[1px] w-full bg-[#E0E0E0]"></div>
        {postInfo.allowUploadedFile && !postInfo.isOwner && (
          <>
            <div className="px-4">
              <UploadApplicationsButton />
            </div>
            <div className="my-3 h-[1px] w-full bg-[#E0E0E0]"></div>
          </>
        )}
        <div className="relative z-[1] grid grid-cols-3">
          <ReactButton />
          <button
            onClick={() => {
              if (isLocked) {
                navigateGroup();
                return;
              }
              setOpenCommentSection((prev) => !prev);
            }}
            className="col-span-1 flex cursor-pointer items-center justify-center gap-2 py-[6px] text-grey_helper"
          >
            <OutlinedChatDots
              className="text-grey_helper"
              width={20}
              height={20}
            />
            <p className="text-sm font-semibold leading-[16px] tracking-[-0.1px]">
              Bình luận
            </p>
          </button>
          <button
            onClick={handleOnClickShare}
            className="col-span-1 flex items-center justify-center gap-2 py-[6px] text-grey_helper"
          >
            <OutlinedSharedNetwork
              className="text-grey_helper"
              width={20}
              height={20}
            />
            <p className="text-sm font-semibold leading-[16px] tracking-[-0.1px]">
              Chia sẻ
            </p>
          </button>
        </div>
      </div>
      <PostCommentSection openCommentSection={openCommentSection} />
    </>
  );
};

const ReactButton = () => {
  const { group, isLocked, ...postInfo } = useContext(PostContext);
  const [isReact, setIsReact] = useState<boolean>(postInfo.isReact);
  const router = useRouter();

  const debounceValue = useDebounce({ value: isReact, delay: 500 });

  const [reactCount, setReactCount] = useState<number>(postInfo.react);

  useEffect(() => {
    setReactCount(postInfo.react);
    setIsReact(postInfo.isReact);
  }, [postInfo.isReact, postInfo.react]);

  const { mutate } = useMutation(putVotePost, {});

  const onClickReact = () => {
    setIsReact((react) => {
      return !react;
    });
    if (isReact) {
      setReactCount((prev) => (prev > 0 ? prev - 1 : 0));
      return;
    }
    setReactCount((prev) => prev + 1);
  };

  const handleReact = () => {
    if (isLocked) {
      router.push({
        pathname: PATH_NAME.MY_GROUPS_DETAIL,
        query: {
          gid: group?.groupId,
        },
      });
      return;
    }

    if (isReact !== postInfo.isReact) {
      mutate({ postId: postInfo.postId, type: EPostVoteType.Love });
    }
  };

  useEffect(() => {
    if (debounceValue !== postInfo.isReact) handleReact();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounceValue]);

  return (
    <div className="col-span-1 flex items-center justify-center gap-2 py-[6px] text-grey_helper">
      <QuickLoginModalHOC
        classname="cursor-pointer"
        handleClickWhenDisabledQuickLogin={onClickReact}
        loginSuccessCb={() => {
          onClickReact();
          if (router.basePath !== PATH_NAME.HOME_FEED_POST_DETAIL) {
            router.push({
              pathname: PATH_NAME.HOME_FEED_POST_DETAIL,
              query: {
                pid: postInfo.postId,
              },
            });
          }
        }}
      >
        {!isReact ? (
          <OutlinedHeart width={20} height={20} className="cursor-pointer" />
        ) : (
          <FilledHeart
            className="cursor-pointer text-[#ED4956]"
            width={20}
            height={20}
          />
        )}
      </QuickLoginModalHOC>
      <p className="text-sm font-semibold leading-[16px] tracking-[-0.1px]">
        {convertQuantity(reactCount)}
      </p>
    </div>
  );
};

export default PostFooter;
